// Generic modal to edit and apply adhoc (system) policies

import {
  Case,
  Switch,
  When,
} from "react-if";
import { policyConfigV2, PolicyType } from "shared";
import { useMemo } from "react";
import { useNavigate } from "react-router";

import { Modal } from "../../../../common/Atoms/Modal";
import { useAdhocActionModal } from "../../../hooks/useAdhocActionModal";
import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { AdhocStatement } from "../../Settings/Policies/AdhocPolicyTypes/AdhocStatement";
import { Heading } from "../../../../common/Atoms/Typography/Heading";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Button } from "../../../../common/Atoms/Button";
import { AdhocInterestCharge } from "../../Settings/Policies/AdhocPolicyTypes/AdhocInterestCharge";
import { useTrialActionLimitReached } from "../../../hooks/useTrialActionsReached";
import { useGetSubscriptionMetadata } from "../../../hooks/useGetSubscription";
import { Notification } from "../../../../common/Atoms/Notification";
import { AdhocInvoiceEmail } from "../../Settings/Policies/AdhocPolicyTypes/AdhocInvoiceEmail";
import { Paragraph } from "../../../../common/Atoms/Typography/Paragraph";
import { useIsSafeMode } from "../../../hooks/useIsSafeMode";
import { AdhocLateFee } from "../../Settings/Policies/AdhocPolicyTypes/AdhocLateFee";
import { AdhocSms } from "../../Settings/Policies/AdhocPolicyTypes/AdhocSms";
import { ActionLimitCTA } from "../../Molecules/ActionLimitCTA";

import { ResultSection } from "./ResultSection";
import { PreviewSection } from "./PreviewSection";
import { NewResultSection } from "./NewResultSection";

export function AdhocActionModal() {
  const { show, close, type, loading, saveAndExecute, result, onPreview, view, isBulk, resultNew, invalids } = useAdhocActionModal();
  const currentOrg = useGetSelectedOrganisation();
  const actionLimitReached = useTrialActionLimitReached();
  const { subscriptionMetadata } = useGetSubscriptionMetadata();
  const navigate = useNavigate();
  const safeModeOn = useIsSafeMode();

  function onStartPlan() {
    if (subscriptionMetadata?.id) {

      close();
      navigate(`/billing/subscriptions/${subscriptionMetadata.id}`);
    }
  }

  const showPreview = useMemo(() => {
    if (!type) return false;

    // Currently cannot preview bulk some actions - other bulk may preview the first one
    if (isBulk) {
      return false;
    }

    return policyConfigV2[type]?.previewConfig?.canPreview;
  }, [type, policyConfigV2]);

  const button = showPreview ? (
    <Button
      onClick={ onPreview }
      color={ `green` }
      disabled={ loading || !!result || !!resultNew }
    >
      { `Preview` }
    </Button>
  ) : (

    <div className={ `flex flex-col items-end` }>
      <Button
        onClick={ saveAndExecute }
        color={ `orange` }
        disabled={ loading || !!result || !!resultNew || actionLimitReached }
      >
        { actionLimitReached ? `Free Action Limit Reached` : `Apply Now` }
      </Button>
      <When condition={ safeModeOn && !isBulk }>
        <Paragraph
          variant={ `help` }
        >
          { `This will apply even in safe mode!` }
        </Paragraph>
      </When>
      <When condition={ isBulk }>
        <Paragraph
          variant={ `help` }
        >
          { `Cannot preview when sending to multiple contacts.` }
        </Paragraph>
      </When>
    </div>
  );

  if (!currentOrg) return null;

  return (
    <Modal
      setOpen={ close }
      open={ show }
      title={ `` }
      width={ `stripe` }
    >
      <div className={ `grid grid-cols-1 sm:grid-cols-2` }>
        <div>
          <LoadingOverlay
            loading={ loading }
            message={ `Updating default policy...` }
          >
            <Switch>
              <Case condition={ type === PolicyType.STATEMENT }>
                <AdhocStatement />
              </Case>

              <Case condition={ type === PolicyType.STATEMENT_LATE_FEE }>
                <AdhocInterestCharge />
              </Case>

              <Case condition={ type === PolicyType.REMINDER }>
                <AdhocInvoiceEmail />
              </Case>

              <Case condition={ type === PolicyType.LATE_FEE }>
                <AdhocLateFee />
              </Case>

              <Case condition={ type === PolicyType.SMS }>
                <AdhocSms />
              </Case>
            </Switch>

            <When condition={ invalids.length > 0 }>
              <div className={ `px-4 mb-4` }>
                <Notification
                  type={ `error` }
                  title={ `Please fix the following before applying:` }
                >
                  <span className={ `flex flex-col` }>
                    { invalids.map(err => (
                      <span key={ err.message }>
                        { err.message }
                      </span>
                    )) }
                  </span>
                </Notification>
              </div>
            </When>

            <div className={ `flex justify-end pr-4` }>
              { button }
            </div>

            <div className={ `flex justify-center` }>
              <ActionLimitCTA
                afterClick={ close }
              />
            </div>
          </LoadingOverlay>
        </div>

        { /* Preview / Result Side side */ }
        <div className={ `border-l-gray-100 border-l pl-3 min-h-full flex flex-col justify-between` }>
          <div className={ `` }>
            <Heading>
              { view === `result` ? `Result` : `Preview` }
            </Heading>

            <When condition={ isBulk }>
              { () => (<NewResultSection />) }
            </When>

            <When condition={ !isBulk }>
              { () => (<ResultSection />) }
            </When>

            <PreviewSection />

          </div>
          { /* Automation CTA */ }
          <div className={ `flex justify-end` }>
            { /* <Button
              color={ `green` }
            >
              { `Create as Automation` }
            </Button> */ }
          </div>
        </div>
      </div>

      <When condition={ actionLimitReached && !!subscriptionMetadata }>
        { () => (
          <div className={ `mt-4` }>
            <Notification
              type={ `warning` }
              buttons={ (
                <Button
                  color={ `green` }
                  onClick={ onStartPlan }
                >
                  { `Start Plan` }
                </Button>
              ) }
            >
              { `You have reached your free trial action limit. Please upgrade to a paid plan to continue.` }
            </Notification>
          </div>
        ) }
      </When>
    </Modal>
  );
}
